import * as React from 'react'
import * as plume from '@ulule/owl-kit-components/next'

import {
  CONTACT_PROJECT_OWNER_URL,
  PRIVACY_EMAIL,
  PRIVACY_POLICY_URL,
  SUBJECT_OPTIONS,
  SUBJECT_PLACEHOLDER,
  ZENDESK_NEW_REQUEST_URL,
} from '../content'
import styles from './ContactForm.module.css'

// Field names — also the FormData keys read on submit.
const SUBJECT = 'subject'
const EMAIL = 'email'
const OBJECT = 'object'
const DESCRIPTION = 'description'

function requiredLabel(text: string): React.ReactNode {
  return (
    <>
      {text} <span className={styles.req}>*</span>
    </>
  )
}

/**
 * Contact form. There is no first-party endpoint — every subject hands off to Zendesk's hosted request
 * form. The subject dropdown selects the ticket form (`ticket_form_id`) and the user's input is carried
 * across as `tf_*` prefill params (the Help Center theme populates the matching fields), so nothing is
 * re-typed. The user completes any form-specific required fields and submits there — submitting through
 * the hosted form is what lets Zendesk's triggers/automations run (an API-created request would land
 * suspended and skip them). Attachments are left to the hosted form (a file can't ride in the URL).
 */
export function ContactForm(): React.ReactElement {
  function handleSubmit(event: React.FormEvent<HTMLFormElement>): void {
    event.preventDefault()
    const data = new FormData(event.currentTarget)
    const ticketFormId = String(data.get(SUBJECT) ?? '')
    const object = String(data.get(OBJECT) ?? '')
    const description = String(data.get(DESCRIPTION) ?? '')
    const email = String(data.get(EMAIL) ?? '')

    // Carry the input into the hosted form as prefill params — field ids match our Help Center theme's
    // tf_* handler: tf_subject → #request_subject, tf_description → #request_description,
    // tf_anonymous_requester_email → #request_anonymous_requester_email.
    const params = new URLSearchParams({ ticket_form_id: ticketFormId })
    if (object !== '') {
      params.set('tf_subject', object)
    }
    if (description !== '') {
      params.set('tf_description', description)
    }
    if (email !== '') {
      params.set('tf_anonymous_requester_email', email)
    }
    window.location.assign(`${ZENDESK_NEW_REQUEST_URL}?${params.toString()}`)
  }

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      <div className={styles.warn}>
        <strong>Attention&nbsp;:</strong> vous vous apprêtez à contacter l'équipe Ulule. Pour toute question
        sur un projet hébergé, merci de toujours commencer par{' '}
        <a href={CONTACT_PROJECT_OWNER_URL}>contacter directement le porteur de projet</a>.
      </div>

      <plume.Field label={requiredLabel('Quel est le sujet de votre demande ?')} htmlFor="contact-subject">
        <select id="contact-subject" name={SUBJECT} defaultValue="" required>
          <option value="" disabled>
            {SUBJECT_PLACEHOLDER}
          </option>
          {SUBJECT_OPTIONS.map((option) => (
            <option key={option.ticketFormId} value={option.ticketFormId}>
              {option.label}
            </option>
          ))}
        </select>
      </plume.Field>

      <plume.TextField
        label={requiredLabel('Adresse e-mail')}
        name={EMAIL}
        type="email"
        placeholder="votre@email.com"
        required
      />

      <plume.TextField
        label={requiredLabel('Objet')}
        name={OBJECT}
        type="text"
        placeholder="Résumez votre demande en quelques mots"
        required
      />

      <plume.TextAreaField
        label={requiredLabel('Description')}
        name={DESCRIPTION}
        placeholder="Saisissez les détails de votre demande. Un membre de notre équipe d'assistance répondra dans les plus brefs délais."
        rows={5}
        required
      />

      <plume.RoundButton type="submit" color="yellow" size="big">
        Continuer
      </plume.RoundButton>

      <p className={styles.hint}>
        Vous finaliserez votre demande sur notre centre d'aide&nbsp;: vos réponses y seront déjà
        pré-remplies.
      </p>

      <p className={styles.privacy}>
        Ulule collecte vos données pour permettre de répondre à vos questions et d'assurer la relation
        clients. Pour exercer vos droits, écrivez à <a href={`mailto:${PRIVACY_EMAIL}`}>{PRIVACY_EMAIL}</a>.
        Pour plus de détails&nbsp;: <a href={PRIVACY_POLICY_URL}>Politique de Confidentialité d'Ulule</a>.
      </p>
    </form>
  )
}
